import React, { useState } from "react";
//Common
import PageHeader from "../common/PageHeader/PageHeader";
import { MoreActionButton } from "../common/Buttons";
import DisplayData from "../common/DisplayData/DisplayData";
import {
  Snackbar,
  Drawer,
  ConfirmationDialog,
  DataGrid,
  Chip,
} from "../common/VFX-UI";

//App
import { UserFormContainer } from "./UserFormContainer";
import { FormikTypeaheadOption, SearchProps, UserProps } from "../../types";
import UserView from "./UserView";
import useUser from "./hooks/useUser";
import SearchContainer from "../common/Forms/SearchContainer/SearchContainer";
import { LookupDto, UserDto } from "../../app/model/api/types/dto";
import { SearchDefaultValue } from "../../store/types";
import { SearchFieldsDefaultValue } from "../../utility/constants/constants";
import { UserPermissionsFormContainer } from "./UserPermissionsFormContainer";
import ResendVerification from "./ResendVerification";
import UserMoreActionsMenu from "./UserMoreActionsMenu";
import { IsCauwUser, hasRole, roles } from "../../utility/helpers/claimHelper";
import { getRole } from "../../utility/helpers";
import { ColumnDescription } from "react-bootstrap-table-next";

const Users: React.FC<UserProps> = (props) => {
  const {
    organizationId,
    users,
    activateMutation,
    deleteMutation,
    resendConfirmationEmail,
    unlockUserMutation,
    resetPasswordMutation,
    fullscreen,
  } = props;
  const [claimsState, setClaimsState] = useState(false);
  const [resendVerificationDialog, setResendVerificationDialog] =
    useState(false);
  const [unlockUser, setUnlockUser] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const {
    state,
    formContext,
    rowEvents,
    actionColumnAnchorEl,
    toggleNewViewHandler,
    toggleViewHandler,
    toggleEditViewHandler,
    toggleActivateHandler,
    toggleDeactivateHandler,
    onShowSuccess,
    onAddSnackbarClose,
    handleActionClick,
    setActionColumnAnchorEl,
  } = useUser({
    deleteMutation: deleteMutation,
    activateMutation: activateMutation,
    resendConfirmationEmail: resendConfirmationEmail,
    unlockUserMutation: unlockUserMutation,
    resetPasswordMutation: resetPasswordMutation,
  });

  const name = `${formContext?.data?.firstName} ${formContext?.data?.lastName}`;

  const openMenu = Boolean(actionColumnAnchorEl);
  var columns: ColumnDescription[] = [
    {
      dataField: "id",
      text: "ID",
      hidden: true,
    },
    {
      dataField: "firstName",
      text: "Name",
      formatter: (cell: any, row: any) => {
        const name = row.firstName + " " + row.lastName;
        return name;
      },
      sort: true,
    },
    {
      dataField: "role",
      formatter: (cell: string | null, row: UserDto) =>
        cell ??
        getRole(row.roleId, formContext?.lookupsData)?.display ??
        getRole(row.roleId, formContext?.lookupsData)?.name ??
        "",
      text: "Role",
      sort: true,
    },
  ];

  if (fullscreen === null || fullscreen === undefined || fullscreen) {
    columns = [
      ...columns,
      {
        dataField: "email",
        text: "Email",
        sort: true,
      },
      {
        dataField: "phoneNumber",
        text: "Phone Number",
        sort: true,
      },
    ];
  } else {
    columns = [
      ...columns,
      {
        dataField: "contact",
        text: "Contact",
        sort: false,
        formatter: (cell: boolean, row: any) => {
          return (
            <>
              <DisplayData text="Email" importedData={row?.email} />
              <DisplayData text="Phone" importedData={row?.phoneNumber} />
            </>
          );
        },
      },
    ];
  }

  columns = [
    ...columns,
    {
      dataField: "status",
      text: "Status",
      sort: false,
      formatter: (cell: boolean, row: any) => {
          return <Chip label={row.status} />;
      },
    },
    {
      dataField: "action",
      text: "",
      style: { width: "25px" },
      isDummyField: true,
      formatter: (cell: unknown, row: UserDto) => {
        return (
          <MoreActionButton
            handleActionClick={(e) => {
              e.stopPropagation();
              if (row.id) {
              void formContext?.setSelectedUser(row);
                void formContext?.setId(row.id);
              }
              handleActionClick(e);
            }}
          />
        );
      },
    },
  ];

  const handleStatusChange = async (options: FormikTypeaheadOption[]) => {
    const statusId =
      options.length > 0
        ? String((options[0] as Record<string, string | number>).value)
        : null;
    await formContext?.setStatusId(statusId);
  };

  const searchProps: SearchProps<UserDto> = {
    dropdown: {
      options: formContext?.statusOptions ?? [],
      afterName: false,
      defaultSelected: formContext?.statusId ? [formContext.statusId] : [],
      clearButton: true, name: "statusId", label: "Status", onChange: handleStatusChange,
    },
    searchValue: formContext?.searchValue ?? SearchDefaultValue,
    searchFields: { ...SearchFieldsDefaultValue, name: true },
    setSearchValue: formContext?.setSearchValue,
    refetchGrid: formContext?.refetchGrid,
  };

  const handleActivateUser = () => {
    if (formContext?.id) {
      toggleActivateHandler();
      if (activateMutation)
        activateMutation(formContext?.id, {
          onSuccess: () => {
            onShowSuccess(`${name} activated.`);
          },
        });
    }
  };

  const handleDeactivateUser = () => {
    if (formContext?.id) {
      toggleDeactivateHandler();
      deleteMutation(formContext?.id, {
        onSuccess: () => {
          onShowSuccess(`${name} deactivated.`);
        },
      });
    }
  };

  const toggleClaimHandler = () => {
    setClaimsState((prevState) => !prevState);
  };

  const handleResendVerification = () => {
    setActionColumnAnchorEl(null);
    setResendVerificationDialog((prevState) => !prevState);
  };

  const handleResendVerificationConfirmation = () => {
    setResendVerificationDialog((prevState) => !prevState);
    resendConfirmationEmail(
      {
        userId: formContext?.id,
        returnUrl: window.location.href,
      },
      {
        onSuccess(data: any) {
          if (data) {
            onShowSuccess("Confirmation Email sent.");
          }
        },
      }
    );
  };

  const handleResetPassword = () => {
    if (formContext?.id) {
      toggleResetPasswordHandler();
      resetPasswordMutation(
        { userId: formContext?.id },
        {
          onSuccess: () => {
            onShowSuccess(`Email has been successfully sent.`);
          },
        }
      );
    }
  };

  const handleUnlockUser = () => {
    if (formContext?.id) {
      toggleUnlockUserHandler();
      unlockUserMutation(
        { userId: formContext?.id },
        {
          onSuccess: () => {
            onShowSuccess(`Account has been unlocked.`);
          },
        }
      );
    }
  };

  const toggleUnlockUserHandler = () => {
    setUnlockUser((prevState) => !prevState);
  };

  const toggleResetPasswordHandler = () => {
    setResetPassword((prevState) => !prevState);
  };

  const handlePageChange = async (
    newPage: number,
    sortField: string | undefined,
    sortOrder: string | undefined
  ) => {
    if (formContext?.setCurrentPage && newPage) {
      await formContext?.setCurrentPage(newPage);
    }
    if (formContext?.setSortBy && sortField && sortOrder) {
      await formContext?.setSortBy({
        field: sortField,
        direction: sortOrder === "asc" ? 1 : -1,
      });
    }
    formContext?.refetchGrid();
  };

  const showResendVerificationLink = (): boolean => {
    const emailConfirmed: boolean = formContext?.data?.emailConfirmed ?? false;
    return !emailConfirmed;
  };

  const AllowNewUsers = () =>
    IsCauwUser() && (organizationId === null || organizationId === undefined);

  return (
    <>
      <div className="d-flex flex-column">
        <div className="flex-fill">
          <PageHeader>Users</PageHeader>
          <SearchContainer
            searchProps={searchProps}
            toggleNewViewHandler={
              AllowNewUsers() ? toggleNewViewHandler : undefined
            }
          />
        </div>
        <div className="flex-fill flex-grow-1">
          <DataGrid
            columns={columns}
            data={users}
            defaultSorted={[
              {
                dataField: "firstName",
                order: "asc",
              },
            ]}
            keyField="id"
            rowEvents={rowEvents}
            handlePageChange={async (
              newPage,
              pageSize,
              sortField,
              sortOrder
            ) => {
              handlePageChange(newPage, sortField, sortOrder);
            }}
          />
        </div>
      </div>

      <Drawer show={state.toggleNewForm}>
        {state.toggleNewForm && (
          <UserFormContainer
            organizationId={organizationId}
            onClose={toggleNewViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleEditForm}>
        {state.toggleEditForm && formContext?.data && (
          <UserFormContainer
            organizationId={organizationId}
            onClose={toggleEditViewHandler}
            onShowSuccess={onShowSuccess}
          />
        )}
      </Drawer>

      <Drawer show={state.toggleView}>
        {state.toggleView && formContext?.data && (
          <UserView
            onClose={toggleViewHandler}
            editAction={() => {
              toggleEditViewHandler();
            }}
            toggleDeactivateHandler={toggleDeactivateHandler}
            toggleActivateHandler={toggleActivateHandler}
            toggleResetPasswordHandler={toggleResetPasswordHandler}
          />
        )}
      </Drawer>

      <Drawer show={claimsState}>
        {claimsState && formContext?.data && (
          <>
            <UserPermissionsFormContainer
              onClose={toggleClaimHandler}
              onShowSuccess={onShowSuccess}
            />
          </>
        )}
      </Drawer>

      {actionColumnAnchorEl && (
        <UserMoreActionsMenu
          isOpen={openMenu}
          target={actionColumnAnchorEl}
          toggle={() => {
            setActionColumnAnchorEl(null);
          }}
          onViewClick={() => {
            toggleViewHandler();
            setActionColumnAnchorEl(null);
          }}
          onPermissionClick={
            hasRole([roles.SYSTEM_ADMIN]) && formContext?.data?.isActive
              ? () => {
                  toggleClaimHandler();
                  setActionColumnAnchorEl(null);
                }
              : undefined
          }
          onResendVerificationClick={
            formContext?.data?.canResendConfirmation
              ? showResendVerificationLink()
                ? handleResendVerification
                : undefined
              : undefined
          }
          onUnlockUserClick={
            formContext?.data?.canUnlock
              ? () => (toggleUnlockUserHandler(), setActionColumnAnchorEl(null))
              : undefined
          }
          onResetPasswordClick={
            formContext?.data?.canResetPassword
              ? () => {
                  toggleResetPasswordHandler();
                  setActionColumnAnchorEl(null);
                }
              : undefined
          }
        />
      )}

      <ConfirmationDialog
        open={state.isDeactivateDialogOpen}
        onClose={toggleDeactivateHandler}
        onConfirm={handleDeactivateUser}
        contentTitle={`Deactivate User`}
        contentText={`Are you sure you want to deactivate ${name}?`}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      ></ConfirmationDialog>

      <ConfirmationDialog
        open={state.isActivateDialogOpen}
        onClose={toggleActivateHandler}
        onConfirm={handleActivateUser}
        contentTitle={`Activate User`}
        contentText={`Are you sure you want to activate ${name}?`}
        confirmText={"Yes, Continue"}
        closeText={"No, Go Back"}
      />
      {state.showSuccessSnackbar && (
        <Snackbar
          open={state.showSuccessSnackbar}
          message={state.successMessage}
          handleClose={onAddSnackbarClose}
          severity="success"
        />
      )}

      {resendVerificationDialog && (
        <ResendVerification
          userName={name}
          isResendVerification={resendVerificationDialog}
          handleOnClose={() => setResendVerificationDialog(false)}
          handleOnConfirm={handleResendVerificationConfirmation}
        />
      )}

      <ConfirmationDialog
        open={unlockUser}
        onClose={toggleUnlockUserHandler}
        onConfirm={handleUnlockUser}
        contentTitle={`Unlock Account`}
        contentText={`Do you wish to unlock ${formContext?.data?.firstName}, ${formContext?.data?.lastName}'s account?`}
        confirmText={"Yes"}
        closeText={"No"}
      ></ConfirmationDialog>

      <ConfirmationDialog
        open={resetPassword}
        onClose={toggleResetPasswordHandler}
        onConfirm={handleResetPassword}
        contentTitle={`Reset Password`}
        contentText={`Are you sure you want to reset ${formContext?.data?.firstName} ${formContext?.data?.lastName} password?  An email will be sent to the user with the instructions to change password.`}
        confirmText={"Yes"}
        closeText={"No"}
      ></ConfirmationDialog>
    </>
  );
};
export default Users;
